import { useState } from "react";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, AlertCircle, ImageIcon } from "lucide-react";
import { trpc } from "@/lib/trpc";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { createFilialSchema, type CreateFilialFormData } from "@/lib/validation/filial";
import { extractStructureState } from "@/types/structure";
import { unmask } from "@/lib/masks";

interface CreateFilialDrawerProps {
  isOpen: boolean;
  company: any;
  onClose: () => void;
  onSuccess?: (filial: any) => void;
  onError?: (error: string) => void;
}

/**
 * Drawer para criar uma nova filial
 * 
 * Regras:
 * - Formulário completo com campos obrigatórios
 * - matrizId é read-only e herdado da empresa atual
 * - controladoraId é herdado automaticamente se existir
 * - Validação completa com Zod
 * - Sem alterar banco/migrations
 */
export default function CreateFilialDrawer({
  isOpen,
  company,
  onClose,
  onSuccess,
  onError,
}: CreateFilialDrawerProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const structureState = extractStructureState(company);

  // Inicializar form com react-hook-form + Zod
  const form = useForm<CreateFilialFormData>({
    resolver: zodResolver(createFilialSchema),
    defaultValues: {
      nomeFantasia: "",
      razaoSocial: "",
      cnpj: "",
      logradouro: "",
      numero: "",
      complemento: "",
      bairro: "",
      cidade: "",
      estado: "",
      cep: "",
      cnae: "",
      atividadePrincipal: "",
      grauRisco: "",
      sector: "",
      employeesCount: 0,
      email: "",
      phone: "",
      responsibleName: "",
      // Campos automáticos (garantir valores sempre definidos)
      matrizId: company?.id || 0,
      controladoraId: structureState.controladoraId || 0,
    },
  });

  const utils = trpc.useUtils();

  // Mutation para criar filial via tRPC
  const createFilialMutation = trpc.company.createFilial.useMutation({
    onSuccess: (filial) => {
      // Verificar se foi vinculação de registro existente ou criação nova
      const wasLinked = (filial as any)._linkedExisting === true;
      if (wasLinked) {
        toast.success("Filial vinculada com sucesso! (registro existente reativado)");
      } else {
        toast.success("Filial criada com sucesso!");
      }
      // Invalidar query de filiais para atualizar a lista
      utils.company.getMatrizAndFiliais.invalidate({ matrizId: company.id });
      // Callback
      onSuccess?.(filial);
      // Resetar form
      form.reset();
      // Fechar drawer
      onClose();
    },
    onError: (error: any) => {
      const errorMessage = error?.message || "Erro ao criar filial. Verifique os dados e tente novamente.";
      setSubmitError(errorMessage);
      toast.error(errorMessage);
      onError?.(errorMessage);
    },
  });

  const handleSubmit = async (data: CreateFilialFormData): Promise<void> => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);

      // Validar matrizId antes de enviar
      if (!company?.id) {
        throw new Error("Empresa Matriz não identificada");
      }
      // Construir payload com tipos explícitos
      const payload = {
        matrizId: company.id,
        data: {
          nomeFantasia: data.nomeFantasia,
          razaoSocial: data.razaoSocial,
          cnpj: unmask(data.cnpj),
          logradouro: data.logradouro,
          numero: data.numero,
          complemento: data.complemento || null,
          bairro: data.bairro,
          cidade: data.cidade,
          estado: data.estado,
          cep: data.cep,
          cnae: data.cnae || null,
          atividadePrincipal: data.atividadePrincipal || null,
          grauRisco: data.grauRisco || null,
          sector: data.sector || null,
          employeesCount: data.employeesCount || null,
          email: data.email || null,
          phone: data.phone || null,
          responsibleName: data.responsibleName || null,
        },
      };
      await createFilialMutation.mutateAsync(payload);
    } catch (error) {
      // Erro já tratado no onError do mutation
      console.error("❌ Erro ao criar filial:", error);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Drawer open={isOpen} onOpenChange={onClose}>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>Criar Nova Filial</DrawerTitle>
          <DrawerDescription>
            Preencha os dados da nova filial. Os campos obrigatórios estão marcados com *.
          </DrawerDescription>
        </DrawerHeader>

        <div className="max-h-[80vh] overflow-y-auto px-4 pb-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
              {/* Erro geral */}
              {submitError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{submitError}</AlertDescription>
                </Alert>
              )}

              {/* Informação sobre herança */}
              <Alert>
                <AlertDescription>
                  <strong>Matriz:</strong> {company?.nomeFantasia}
                  {structureState.controladoraId && (
                    <>
                      <br />
                      <strong>Grupo:</strong> Esta filial herdará o vínculo ao grupo econômico.
                    </>
                  )}
                </AlertDescription>
              </Alert>

              {/* Seção: Dados Básicos */}
              <div className="space-y-4">
                <h3 className="font-semibold text-base">Dados Básicos</h3>

                <FormField
                  control={form.control}
                  name="nomeFantasia"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nome Fantasia *</FormLabel>
                      <FormControl>
                        <Input placeholder="Ex: Filial São Paulo" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="razaoSocial"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Razão Social *</FormLabel>
                      <FormControl>
                        <Input placeholder="Ex: Empresa LTDA" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="cnpj"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CNPJ *</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Ex: 12.345.678/0001-99"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Formato: 12.345.678/0001-99 ou 12345678000199
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Seção: Endereço */}
              <div className="space-y-4">
                <h3 className="font-semibold text-base">Endereço</h3>

                <FormField
                  control={form.control}
                  name="logradouro"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Logradouro *</FormLabel>
                      <FormControl>
                        <Input placeholder="Ex: Rua das Flores" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="numero"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Número *</FormLabel>
                        <FormControl>
                          <Input placeholder="Ex: 123" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="complemento"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Complemento</FormLabel>
                        <FormControl>
                          <Input placeholder="Ex: Apto 456" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="bairro"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Bairro *</FormLabel>
                      <FormControl>
                        <Input placeholder="Ex: Centro" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="cidade"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Cidade *</FormLabel>
                        <FormControl>
                          <Input placeholder="Ex: São Paulo" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="estado"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Estado *</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {[
                              "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES",
                              "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR",
                              "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC",
                              "SP", "SE", "TO"
                            ].map((state) => (
                              <SelectItem key={state} value={state}>
                                {state}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="cep"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CEP *</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Ex: 01310-100"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Formato: 12345-678 ou 12345678
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Seção: NR-1 (Opcional) */}
              <div className="space-y-4">
                <h3 className="font-semibold text-base">Dados NR-1 (Opcional)</h3>

                <FormField
                  control={form.control}
                  name="cnae"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CNAE</FormLabel>
                      <FormControl>
                        <Input placeholder="Ex: 4711-3/00" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="atividadePrincipal"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Atividade Principal</FormLabel>
                      <FormControl>
                        <Input placeholder="Ex: Comércio varejista" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="grauRisco"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Grau de Risco</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="baixo">Baixo</SelectItem>
                          <SelectItem value="medio">Médio</SelectItem>
                          <SelectItem value="alto">Alto</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="sector"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Setor</FormLabel>
                      <FormControl>
                        <Input placeholder="Ex: Comércio" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="employeesCount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Quantidade de Colaboradores</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="Ex: 50"
                          {...field}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value ? parseInt(e.target.value) : undefined
                            )
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Seção: Contato (Opcional) */}
              <div className="space-y-4">
                <h3 className="font-semibold text-base">Contato (Opcional)</h3>

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="Ex: contato@filial.com"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Telefone</FormLabel>
                      <FormControl>
                        <Input placeholder="Ex: (11) 3000-0000" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="responsibleName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Responsável</FormLabel>
                      <FormControl>
                        <Input placeholder="Ex: João Silva" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Seção: Campos Automáticos (Read-only) */}
              <div className="space-y-4 bg-muted/50 p-4 rounded-lg">
                <h3 className="font-semibold text-base">Campos Automáticos</h3>

                <div>
                  <Label className="text-muted-foreground">Matriz (matrizId)</Label>
                  <p className="text-sm font-mono mt-1">
                    {company?.id || "Será preenchido automaticamente"}
                  </p>
                </div>

                {structureState.controladoraId && (
                  <div>
                    <Label className="text-muted-foreground">
                      Controladora (controladoraId)
                    </Label>
                    <p className="text-sm font-mono mt-1">
                      {structureState.controladoraId}
                    </p>
                  </div>
                )}
              </div>

              {/* Seção: Logo da Filial (informativa) */}
              <div className="space-y-3 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <ImageIcon className="w-4 h-4" />
                  <h3 className="text-sm font-semibold">Logo da Filial</h3>
                </div>
                <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <p className="text-sm text-blue-800">
                    Você poderá anexar a logo após salvar a filial.
                  </p>
                </div>
              </div>

              {/* Botões */}
              <div className="flex gap-3 pt-4">
                <DrawerClose asChild>
                  <Button variant="outline" disabled={isSubmitting}>
                    Cancelar
                  </Button>
                </DrawerClose>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Criando...
                    </>
                  ) : (
                    "Criar Filial"
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
